import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { ApplicationState, AppState } from '../../../store';
import { withSnackbar, ProviderContext } from 'notistack';
import { ITranslatorService } from '../../../services/Interfaces/ITranslatorService';
import { ApplicationContext, AppContext } from '../../../context/Contexts';
import { IOrganizationService } from '../../../services/Interfaces/IOrganizationService';
import { IReferentialService } from '../../../services/Interfaces/IReferentialService';
import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  Grid,
  Typography,
  DialogContent,
  Tooltip
} from '@material-ui/core';
import { ScaleLoader } from 'react-spinners';
import AddCircleTwoToneIcon from '@material-ui/icons/AddCircleTwoTone';
import { RouteComponentProps } from 'react-router';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  GetOrganizationsByTypeRequestDto,
  GetOrganizationProviderListRequestDto,
  AddOrganizationProviderRequestDto,
  Organization,
  OrganizationProvider
} from '../../../interfaces/Organization';
import { createDefaultOrganizationProvider } from '../../../utils/organization';
import { IReferential } from '../../../interfaces/IReferential';
import MUIDataTable, { SelectableRows, MUIDataTableMeta } from 'mui-datatables';
import { MUITranslations } from '../../../helpers/MUITableTranslations';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineTwoToneIcon from '@material-ui/icons/DeleteOutlineTwoTone';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import NumberFormat from 'react-number-format';
import Referentials from '../../../helpers/Referentials.json';
import {
  DEFAULT_PAYMENT_DAYS,
  OrganizationTypeCode,
  PaymentMethodCodeEnum,
  ProviderCategoryCodeEnum,
  ReferentialCode
} from '../../../helpers/Constants';

interface IOrganizationProviderState {
  newOrganizationProvider: OrganizationProvider | null;
  organizationProviders: OrganizationProvider[];
  organizations: Organization[];
  paymentMethods: IReferential[];
  isLoading: boolean;
  isDialogOpen: boolean;
  organization: Organization | null;
  organizationProviderId: number | null;
  providerCategoryId: number;
}

type IOrganizationProviderProps = { appState: AppState } & ProviderContext &
  RouteComponentProps<{ id: string }>;

class OrganizationProviders extends React.PureComponent<
  IOrganizationProviderProps,
  IOrganizationProviderState
> {
  private translatorService!: ITranslatorService;
  private organizationService!: IOrganizationService;
  private appReferentialService!: IReferentialService;

  static contextType = ApplicationContext;

  state = {
    newOrganizationProvider: null,
    organizationProviders: [],
    organizations: [],
    paymentMethods: [],
    isLoading: false,
    isDialogOpen: false,
    organization: null,
    organizationProviderId: null,
    providerCategoryId: 0
  } as IOrganizationProviderState;

  public componentDidMount() {
    const orgId = Number.parseInt(this.props.match.params.id);

    if (Number.isNaN(orgId)) {
      return;
    }

    this.setState(
      {
        isLoading: true
      },
      async () => {
        await this.loadData(orgId);
      }
    );
  }

  loadData = async (orgId: number) => {
    const paymentMethodRef = Referentials.referential.find(
      (item) => item.code === ReferentialCode.PaymentMethod
    );
    const providerCategoryRef = Referentials.referential.find(
      (item) => item.code === ReferentialCode.ProviderCategoryType
    );

    const org = await this.organizationService.GetOrganization(orgId);

    const fleetOrgRequest: GetOrganizationsByTypeRequestDto = {
      organizationTypes: [OrganizationTypeCode.FLOTA],
      countryIds: org.countryId ? [org.countryId] : []
    };

    const [fleetOrgs, paymentMethods, providerCategories] = await Promise.all([
      this.organizationService.GetOrganizationsByType(fleetOrgRequest),
      this.appReferentialService.Get(paymentMethodRef!.baseUrl),
      this.appReferentialService.Get(providerCategoryRef!.baseUrl)
    ]);

    const providerCategory = providerCategories.find(
      (c) => c.code === ProviderCategoryCodeEnum.PARTENER_FLOTA
    );

    if (!providerCategory) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
      this.setState({ isLoading: false });
      return;
    }

    const providerListRequest: GetOrganizationProviderListRequestDto = {
      organizationId: orgId,
      providerCategoryId: providerCategory.id,
      partnerId: null
    };

    const providers = await this.organizationService.GetOrganizationProviders(providerListRequest);

    providers.forEach((p) => {
      p.partner = fleetOrgs.find((o) => o.id === p.partnerId) ?? null;
      p.paymentMethod = paymentMethods.find((m) => m.id === p.paymentMethodId) ?? null;
    });

    this.setState({
      organizationProviders: providers,
      organizations: fleetOrgs,
      paymentMethods,
      providerCategoryId: providerCategory.id,
      organization: org,
      isLoading: false
    });
  };

  newOrganizationProvider = async (providerId: number | null) => {
    const { organizationProviders, organization, providerCategoryId, paymentMethods } = this.state;

    let newOrganizationProvider: OrganizationProvider;

    if (providerId === null) {
      const bankTransferMethod = paymentMethods.find(
        (m) => m.code === PaymentMethodCodeEnum.BANK_TRANSFER
      );
      newOrganizationProvider = createDefaultOrganizationProvider(
        organization?.id ?? null,
        providerCategoryId,
        bankTransferMethod?.id ?? 1,
        DEFAULT_PAYMENT_DAYS
      );
      newOrganizationProvider.paymentMethod = bankTransferMethod ?? null;
    } else {
      newOrganizationProvider = organizationProviders.find((item) => item.id === providerId)!;
    }

    this.setState({
      newOrganizationProvider,
      organizationProviderId: providerId,
      isDialogOpen: true
    });
  };

  closeDialog = async () => {
    this.setState({ isDialogOpen: false });
  };

  saveProvider = async () => {
    const { organizationProviderId, newOrganizationProvider, organization } = this.state;

    if (!newOrganizationProvider || !newOrganizationProvider.partner) return;

    if (organizationProviderId === null) {
      if (
        this.state.organizationProviders.findIndex(
          (item) =>
            item.partnerId === newOrganizationProvider.partnerId &&
            item.providerCategoryId === newOrganizationProvider.providerCategoryId
        ) !== -1
      ) {
        this.props.enqueueSnackbar(
          this.translatorService.Tranlate(
            'ERROR_MSG_ORGANIZATION_PARTNER_EXISTS',
            'Partenerul exista deja!'
          ),
          { variant: 'error' }
        );
        return;
      }
    } else {
      if (
        this.state.organizationProviders.findIndex(
          (item) =>
            item.partnerId === newOrganizationProvider.partnerId &&
            item.providerCategoryId === newOrganizationProvider.providerCategoryId &&
            item.id !== organizationProviderId
        ) !== -1
      ) {
        this.props.enqueueSnackbar(
          this.translatorService.Tranlate(
            'ERROR_MSG_ORGANIZATION_PARTNER_EXISTS',
            'Partenerul exista deja!'
          ),
          { variant: 'error' }
        );
        return;
      }
    }

    const request: AddOrganizationProviderRequestDto = {
      id: organizationProviderId ?? 0,
      organizationId: organization?.id ?? null,
      partnerId: newOrganizationProvider.partnerId,
      providerCategoryId: newOrganizationProvider.providerCategoryId,
      paymentMethodId: newOrganizationProvider.paymentMethodId,
      paymentDays: newOrganizationProvider.paymentDays
    };

    try {
      await this.organizationService.AddOrganizationProvider(request);

      this.setState(
        {
          isLoading: true,
          isDialogOpen: false
        },
        async () => await this.loadData(organization!.id)
      );

      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (error) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  handlePartnerChange = (newValue: Organization | null) => {
    this.setState({
      newOrganizationProvider: {
        ...this.state.newOrganizationProvider!,
        partnerId: newValue?.id ?? null,
        partner: newValue
      }
    });
  };

  handlePaymentMethodChange = (newValue: IReferential | null) => {
    const isCard = newValue?.code === PaymentMethodCodeEnum.CARD;
    this.setState({
      newOrganizationProvider: {
        ...this.state.newOrganizationProvider!,
        paymentMethodId: newValue?.id ?? 1,
        paymentMethod: newValue,
        paymentDays: isCard ? 0 : this.state.newOrganizationProvider!.paymentDays
      }
    });
  };

  handlePaymentDaysChange = (value: string) => {
    this.setState({
      newOrganizationProvider: {
        ...this.state.newOrganizationProvider!,
        paymentDays: value === null || value === '' ? 0 : parseInt(value)
      }
    });
  };

  NumberFormatCustom = (props: any) => {
    const { inputRef, onChange, ...other } = props;

    return (
      <NumberFormat
        {...other}
        getInputRef={inputRef}
        onValueChange={(values: any) => {
          onChange({
            target: {
              value: values.value
            }
          });
        }}
        decimalScale={0}
        isNumericString
      />
    );
  };

  deleteProvider = async (id: number) => {
    try {
      await this.organizationService.RemoveOrganizationProvider(id);

      this.setState(
        { isLoading: true },
        async () => await this.loadData(this.state.organization!.id)
      );

      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (error) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  getColumns = () => {
    const { organizationProviders, paymentMethods } = this.state;

    return [
      {
        name: 'id',
        options: { display: 'excluded' as const, filter: false }
      },
      {
        name: 'partnerId',
        label: this.translatorService.Tranlate('ORGANIZATION_PROVIDER_PARTNER', 'Partener'),
        options: {
          customBodyRender: (value: number, tableMeta: MUIDataTableMeta) => {
            const p = organizationProviders.find((x) => x.id === tableMeta.rowData[0]);
            return <span>{p?.partner?.name}</span>;
          }
        }
      },
      {
        name: 'paymentMethodId',
        label: this.translatorService.Tranlate('PAYMENT_METHOD_ORGANIZATION_LABEL', 'Metoda plata'),
        options: {
          customBodyRender: (value: number) => {
            const pm = paymentMethods.find((m) => m.id === value);
            return pm?.displayName ?? '-';
          }
        }
      },
      {
        name: 'paymentDays',
        label: this.translatorService.Tranlate(
          'PAYMENT_DAYS_ORGANIZATION_LABEL',
          'Termen plata (zile)'
        )
      },
      {
        name: '',
        options: {
          viewColumns: false,
          filter: false,
          setCellHeaderProps: () => ({ align: 'center' }),
          setCellProps: () => ({ align: 'center' }),
          customBodyRender: (value: any, tableMeta: MUIDataTableMeta) => {
            const id = tableMeta.rowData[0];
            return (
              <Fragment>
                <IconButton
                  aria-label="edit"
                  color="inherit"
                  className="text-primary"
                  size="small"
                  onClick={() => this.newOrganizationProvider(id)}
                >
                  <EditOutlinedIcon />
                </IconButton>
                <IconButton
                  aria-label="delete"
                  color="inherit"
                  className="text-danger"
                  size="small"
                  onClick={() => this.deleteProvider(id)}
                >
                  <DeleteOutlineTwoToneIcon />
                </IconButton>
              </Fragment>
            );
          }
        }
      }
    ];
  };

  renderAddForm = () => {
    const { newOrganizationProvider, organizations, paymentMethods } = this.state;

    if (!newOrganizationProvider) return null;

    const selectedPartner =
      organizations.find((o) => o.id === newOrganizationProvider.partnerId) ?? null;

    return (
      <ValidatorForm onSubmit={(e) => this.saveProvider()}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              id="partner"
              fullWidth
              options={organizations}
              getOptionLabel={(o) => o.name ?? ''}
              value={selectedPartner}
              onChange={(e: any, newValue: Organization | null) =>
                this.handlePartnerChange(newValue)
              }
              renderInput={(params) => (
                <TextValidator
                  {...params}
                  name="partner"
                  value={selectedPartner}
                  label={this.translatorService.Tranlate(
                    'ORGANIZATION_PROVIDER_PARTNER',
                    'Partener'
                  )}
                  fullWidth
                  validators={['required']}
                  errorMessages={[
                    this.translatorService.Tranlate(
                      'VALIDATORS_REQUIRED',
                      'Campul este obligatoriu'
                    )
                  ]}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              id="paymentMethod"
              fullWidth
              options={paymentMethods}
              getOptionLabel={(m) => m.displayName ?? ''}
              value={newOrganizationProvider.paymentMethod}
              onChange={(e: any, newValue: IReferential | null) =>
                this.handlePaymentMethodChange(newValue)
              }
              renderInput={(params) => (
                <TextValidator
                  {...params}
                  name="paymentMethod"
                  value={newOrganizationProvider.paymentMethod}
                  label={this.translatorService.Tranlate(
                    'PAYMENT_METHOD_ORGANIZATION_LABEL',
                    'Metoda plata'
                  )}
                  fullWidth
                  validators={['required']}
                  errorMessages={[
                    this.translatorService.Tranlate(
                      'VALIDATORS_REQUIRED',
                      'Campul este obligatoriu'
                    )
                  ]}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextValidator
              fullWidth
              name="paymentDays"
              id="paymentDays"
              value={newOrganizationProvider.paymentDays ?? ''}
              onChange={(e: any) => this.handlePaymentDaysChange(e.target.value)}
              label={this.translatorService.Tranlate(
                'PAYMENT_DAYS_ORGANIZATION_LABEL',
                'Termen plata (zile)'
              )}
              validators={['required', 'minNumber:0']}
              errorMessages={[
                this.translatorService.Tranlate('VALIDATORS_REQUIRED', 'Campul este obligatoriu'),
                this.translatorService.Tranlate('VALIDATORS_MIN_VALUE', 'Valoare minima 0')
              ]}
              InputProps={{
                inputComponent: this.NumberFormatCustom
              }}
            />
          </Grid>
        </Grid>
        <div className="text-right">
          <Button className="m-2" variant="contained" color="secondary" type="submit">
            {this.translatorService.Tranlate('SAVE', 'Salveaza')}
          </Button>
        </div>
      </ValidatorForm>
    );
  };

  public render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.organizationService = (this.context as AppContext).organizationService;
    this.appReferentialService = (this.context as AppContext).referentialService;

    const options = {
      filter: true,
      selectableRows: 'none' as SelectableRows,
      selectableRowsOnClick: false,
      print: false,
      download: false,
      textLabels: MUITranslations.GetTranslations(this.translatorService),
      sort: false,
      customToolbar: () => {
        return (
          <Tooltip title={this.translatorService.Tranlate('Add', 'Adauga')}>
            <IconButton
              aria-label="add"
              color="secondary"
              className="m-2"
              onClick={() => this.newOrganizationProvider(null)}
            >
              <AddCircleTwoToneIcon />
            </IconButton>
          </Tooltip>
        );
      }
    };

    return (
      <Fragment>
        <Box mt={1} pt={1}>
          <div className="d-flex flex-row text-center flex-wrap justify-content-center">
            <ScaleLoader color={'var(--primary)'} loading={this.state.isLoading} />
          </div>
          {!this.state.isLoading ? (
            <MUIDataTable
              title={''}
              data={this.state.organizationProviders}
              columns={this.getColumns()}
              options={options}
            />
          ) : null}
        </Box>
        <Dialog
          onClose={this.closeDialog}
          aria-labelledby="customized-dialog-title"
          open={this.state.isDialogOpen}
          fullWidth={true}
          maxWidth={'sm'}
        >
          <DialogTitle id="customized-dialog-title">
            <Grid container>
              <Grid item xs={11}>
                <Typography variant="h4">
                  {this.state.organizationProviderId === null
                    ? this.translatorService.Tranlate(
                        'ORGANIZATION_PROVIDER_ADD_TITLE',
                        'Adauga partener'
                      )
                    : this.translatorService.Tranlate(
                        'ORGANIZATION_PROVIDER_UPDATE_TITLE',
                        'Actualizeaza partener'
                      )}
                </Typography>
              </Grid>
              <Grid item xs={1}>
                <IconButton aria-label="close" onClick={this.closeDialog} size={'small'}>
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent dividers>{this.renderAddForm()}</DialogContent>
        </Dialog>
      </Fragment>
    );
  }
}

export default connect(
  (state: ApplicationState) => ({
    appState: state.app
  }),
  null
)(withSnackbar(OrganizationProviders as any));
